'use client';

import { useRef, useState } from 'react';

import type { GroupEventSearchItem } from '@ps/api/types';
import { GroupEventLocationTypeEnumItem } from '@ps/api/types';
import { AccessGuard } from '@ps/features/access-control/components/access-guard/access-guard';
import { useLeadFormGuardContext } from '@ps/features/access-control/components/access-guard/lead-form-guard-context';
import { createGuestAccount } from '@ps/functions/guest-auth';
import type { CreatorInfo } from '@ps/types/creator';
import { Button } from '@ps/ui/atoms/button';
import { Dialog, DialogContent, DialogTitle } from '@ps/ui/atoms/dialog';
import { useCurrentUserId } from '@ps/ui/contexts/auth';
import { withClientAuth } from '@ps/ui/contexts/auth/with-client-auth';
import { Check, Info } from 'lucide-react';

import { useGroupEvents } from '../hooks/use-group-events';
import { EventListSkeleton } from '../skeleton';
import { bookGroupEvent } from '../utils/book-group-event';
import { isEventBooked, markEventBooked } from '../utils/booked-events';
import { formatDateFull, formatTimeRangeFull } from '../utils/format';
import { EventCard } from './event-card';
import { EventDetail } from './event-detail';

// ─── Component ──────────────────────────────────────────────────────────────

interface EventListProps {
  storeId: string;
  listingId: string | undefined;
  /** When true, expanded detail hides edit/delete actions. */
  readOnly?: boolean;
  /** When true, the "Reserve your spot" button is enabled (public view). */
  isPublic?: boolean;
  /** Seller profile for access guard modals (public view). */
  sellerProfile: CreatorInfo;
  /** Optional header rendered above the list when events exist. */
  header?: React.ReactNode;
  /** Whether the creator's Google Calendar is connected. Google Meet events are hidden when false. */
  calendarAuthorized?: boolean;
}

export function EventList({
  storeId,
  listingId,
  readOnly,
  isPublic,
  sellerProfile,
  header,
  calendarAuthorized = true,
}: EventListProps) {
  const { isLoaded: leadFormsLoaded } = useLeadFormGuardContext();
  const currentUserId = useCurrentUserId();
  const {
    events: rawEvents,
    isLoading,
    refetch,
  } = useGroupEvents({
    storeId,
    listingId,
    isPublic,
  });
  // Hide Google Meet events when creator's Google Calendar is disconnected
  const events = calendarAuthorized
    ? rawEvents
    : rawEvents.filter(
        e => e.locationType !== GroupEventLocationTypeEnumItem.GoogleMeet,
      );

  const [expandedEventId, setExpandedEventId] = useState<string | null>(null);
  const [isBooking, setIsBooking] = useState(false);
  const [bookedEvent, setBookedEvent] = useState<GroupEventSearchItem | null>(
    null,
  );
  const [alreadyBookedEvent, setAlreadyBookedEvent] =
    useState<GroupEventSearchItem | null>(null);

  const expandedEvent = events.find(event => event.id === expandedEventId);
  const pendingBookEventRef = useRef<GroupEventSearchItem | null>(null);

  async function doBooking(event: GroupEventSearchItem) {
    if (!listingId) return;
    // Auth loads lazily — `currentUserId` is `undefined` until it resolves.
    // Ignore the tap until then, else a signed-in viewer would be misread as
    // anonymous and a throwaway guest minted for them (booking mis-attributed).
    if (currentUserId === undefined) return;
    setIsBooking(true);
    try {
      // AccessGuard handles guest account creation via the contact form.
      // Fall back to a blank guest only if somehow still unauthenticated.
      const userId = currentUserId;

      if (!userId) {
        await createGuestAccount();
      }

      await bookGroupEvent({
        storeId,
        listingId,
        groupEventId: event.id,
        startDate: event.startDate,
        endDate: event.endDate,
        timezone: event.timezone,
      });

      markEventBooked(storeId, event.id, event.name);
      setBookedEvent(event);
      refetch();
    } catch (error) {
      console.error('[EventList] Booking failed:', error);
    } finally {
      setIsBooking(false);
    }
  }

  function handleSuccessDone() {
    setBookedEvent(null);
    setExpandedEventId(null);
  }

  // Show the list skeleton while lead forms (public view) or events are
  // being fetched — consistent loading shape across both stages.
  if ((isPublic && !leadFormsLoaded) || (isLoading && events.length === 0)) {
    return <EventListSkeleton />;
  }

  if (events.length === 0) {
    return (
      <p className="py-4 text-center text-sm text-gray-400 dark:text-gray-500">
        No upcoming events. Please check back later.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {header}
      {expandedEvent ? (
        <>
          <EventDetail
            event={expandedEvent}
            onBack={() => setExpandedEventId(null)}
            onEdit={readOnly ? undefined : undefined}
            onDelete={readOnly ? undefined : undefined}
            isPublic={isPublic}
          />
          {/* Reserve button for public view (hidden for store owner) */}
          {isPublic && currentUserId !== storeId && (
            <AccessGuard
              storeId={storeId}
              guard={{ kind: 'skip-follow' }}
              sellerProfile={sellerProfile}
              onAccessGranted={() => {
                const event = pendingBookEventRef.current;
                if (event) void doBooking(event);
              }}
            >
              {(canProceed, requestAccess, isReady) => (
                <Button
                  onClick={() => {
                    if (isEventBooked(storeId, expandedEvent.id)) {
                      setAlreadyBookedEvent(expandedEvent);
                      return;
                    }
                    if (canProceed) {
                      void doBooking(expandedEvent);
                      return;
                    }
                    pendingBookEventRef.current = expandedEvent;
                    requestAccess();
                  }}
                  disabled={isBooking || !isReady}
                  variant="ghost"
                  className="bg-mint-green-300 hover:bg-mint-green-400 w-full rounded-[10px] py-2.5 text-center text-sm font-medium text-black transition-colors hover:text-black disabled:opacity-60"
                >
                  {isBooking ? 'Reserving...' : 'Reserve your spot'}
                </Button>
              )}
            </AccessGuard>
          )}
        </>
      ) : (
        events.map(event => (
          <EventCard
            key={event.id}
            event={event}
            onClick={() => setExpandedEventId(event.id)}
          />
        ))
      )}

      {/* Success modal */}
      <Dialog
        open={bookedEvent != null}
        onOpenChange={() => handleSuccessDone()}
      >
        <DialogContent
          showCloseButton={false}
          className="border-border bg-card dark max-w-sm px-6 py-8"
        >
          <DialogTitle className="sr-only">Reservation Confirmed</DialogTitle>
          <div className="flex flex-col items-center gap-4">
            <div className="bg-primary/15 flex h-14 w-14 items-center justify-center rounded-full">
              <Check className="text-primary h-7 w-7" />
            </div>
            <div>
              <h3 className="text-foreground text-center text-lg font-semibold">
                Your spot is reserved!
              </h3>
              {bookedEvent && (
                <div className="text-muted-foreground mt-2 space-y-1 text-center text-sm">
                  <p className="text-foreground/80">{bookedEvent.name}</p>
                  <p>{formatDateFull(bookedEvent.startDate)}</p>
                  <p>
                    {formatTimeRangeFull(
                      bookedEvent.startDate,
                      bookedEvent.endDate,
                    )}
                  </p>
                </div>
              )}
            </div>
            <Button
              onClick={handleSuccessDone}
              className="bg-primary text-primary-foreground hover:bg-primary/90 w-full rounded-full py-3 text-sm font-semibold"
            >
              You're all set!
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Already booked info modal */}
      <Dialog
        open={alreadyBookedEvent != null}
        onOpenChange={() => setAlreadyBookedEvent(null)}
      >
        <DialogContent
          showCloseButton={false}
          className="border-border bg-card dark max-w-sm px-6 py-8"
        >
          <DialogTitle className="sr-only">Already Reserved</DialogTitle>
          <div className="flex flex-col items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-blue-500/15">
              <Info className="h-7 w-7 text-blue-500" />
            </div>
            <div>
              <h3 className="text-foreground text-center text-lg font-semibold">
                You've already reserved this spot
              </h3>
              {alreadyBookedEvent && (
                <p className="text-muted-foreground mt-2 text-center text-sm">
                  You have an existing reservation for{' '}
                  <span className="text-foreground/80 font-medium">
                    {alreadyBookedEvent.name}
                  </span>
                  . Check your email for confirmation details.
                </p>
              )}
            </div>
            <Button
              onClick={() => setAlreadyBookedEvent(null)}
              className="bg-primary text-primary-foreground hover:bg-primary/90 w-full rounded-full py-3 text-sm font-semibold"
            >
              Got it
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default withClientAuth(EventList);
